import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { format, parse } from 'date-fns';

import { B2C, TimePicker } from 'cosmos-components';

import globalMsg from 'src/messages';
import { useGoBack } from 'src/hooks/useGoBack';
import { sciTheme } from 'src/themes';
import * as SShared from 'src/styles/shared.styled';
import { ValidationError } from 'src/components';

import { useArrivalTimeForm } from './hooks/arrivalTime.hooks';
import * as S from './arrivalTime.style';
import msg from './arrivalTime.messages';
import { MODULE_PREFIX } from './arrivalTime.constant';

interface ArrivalTimeFormProps {
    successCallback: () => void;
}

export const ArrivalTimeForm = ({ successCallback }: ArrivalTimeFormProps) => {
    const intl = useIntl();
    const { goBack } = useGoBack();

    const {
        control,
        isLoading,
        isPending,
        operatingHours,
        pickUpBranchName,
        pickUpDate,
        onSubmit,
    } = useArrivalTimeForm(successCallback);

    const formattedPickUpDate = pickUpDate
        ? format(parse(pickUpDate.slice(0, 10), 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
        : '';

    if (isLoading) {
        return <B2C.LoaderSpinner />;
    }

    return (
        <form onSubmit={onSubmit}>
            <B2C.LoaderContainer
                loading={isPending}
                opacity={{ opacity: '90%', color: sciTheme.colors.b2cWhite }}
            >
                <S.ArrivalTimeFormTitle>
                    {intl.formatMessage(msg.form.formTitle)}
                </S.ArrivalTimeFormTitle>
                <S.BranchInfo>
                    <S.BranchInfoRow>
                        <S.Label>{intl.formatMessage(msg.form.pickUpBranch)}</S.Label>
                        <S.Value>{pickUpBranchName}</S.Value>
                    </S.BranchInfoRow>
                    <S.BranchInfoRowStacked>
                        <S.Label>{intl.formatMessage(msg.form.operatingHours, { date: formattedPickUpDate })}</S.Label>
                        <S.Value>{operatingHours.start.open} - {operatingHours.start.close}</S.Value>
                    </S.BranchInfoRowStacked>
                </S.BranchInfo>
                <Controller
                    name="arrivalTime"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <S.AdjacentPairsWrapper>
                            <S.StyledFormField
                                isRequired
                                label={intl.formatMessage(
                                    msg.form.branchArrivalTime,
                                )}
                            >
                                <TimePicker
                                    value={field.value}
                                    onChange={field.onChange}
                                    testId="time-picker"
                                />
                            </S.StyledFormField>
                            <ValidationError
                                data-test-id={`${MODULE_PREFIX}-arrivalTime-error`}
                            >
                                {error?.message}
                            </ValidationError>
                        </S.AdjacentPairsWrapper>
                    )}
                />
                <Controller
                    name="flightNumber"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <S.StyledFormField
                                label={intl.formatMessage(
                                    msg.form.flightNumber,
                                )}
                            >
                                <S.StyledInput
                                    testId={`${MODULE_PREFIX}-flightNumber`}
                                    value={field.value}
                                    placeholder={intl.formatMessage(
                                        msg.form.placeholder,
                                    )}
                                    name="flightNumber"
                                    onInputChange={field.onChange}
                                />
                            </S.StyledFormField>
                        </div>
                    )}
                />
                <SShared.ActionWrapper>
                    <SShared.StyledSecondaryButton
                        data-test-id={`${MODULE_PREFIX}-back`}
                        type="button"
                        onClick={goBack}
                    >
                        {intl.formatMessage(globalMsg.buttons.back)}
                    </SShared.StyledSecondaryButton>
                    <SShared.StyledPrimaryButton
                        data-test-id={`${MODULE_PREFIX}-save`}
                        type="submit"
                    >
                        {intl.formatMessage(msg.buttons.continue)}
                    </SShared.StyledPrimaryButton>
                </SShared.ActionWrapper>
            </B2C.LoaderContainer>
        </form>
    );
};
